/** @jsx isolateComponent */
import { ReactNode } from 'react';
import { css } from '@emotion/core';

import { Animation, Colors, Shadows } from '@zapier/design-system';
import {
  isolateComponent,
  labelStyles,
  withModifier,
} from '@zapier/style-encapsulation';
import { ServiceIconSize } from '../ServiceIcon';
import { ServiceIconConfig } from '../ServiceIcon/constants';

type ServiceIconShellProps = {
  /**
   * The size of ServiceIconShell component to render.
   */
  size: ServiceIconSize;
  /**
   * Applies a shadow to the ServiceIconShell.
   */
  hasShadow?: boolean;
  /**
   * The children to render inside of the ServiceIconShell component.
   */
  children?: ReactNode;
};

const getPropsWithDefaults = (props: ServiceIconShellProps) => ({
  isEmpty: false,
  ...props,
});

const Styles = labelStyles('ServiceIconShell', {
  root: (_props: ServiceIconShellProps) => {
    const props = getPropsWithDefaults(_props);
    const { borderRadius } = ServiceIconConfig[props.size];

    return [
      css`
        display: flex;
        align-items: center;
        justify-content: center;
        background-color: ${Colors.white};
        border: 1px solid ${Colors.gray20};
        border-radius: ${borderRadius}px;
      `,
      withModifier(
        `size`,
        css`
          height: ${props.size}px;
          width: ${props.size}px;
        `
      ),
      props.hasShadow &&
        withModifier(
          'shadowed',
          css`
            box-shadow: ${Shadows.elevation5};
            transition-duration: ${Animation.transitionDuration};
            transition-property: box-shadow;
            transition-timing-function: ${Animation.transitionTimingFunction};
          `
        ),
    ];
  },
});

/**
 * Renders the shell around an icon or number, representing one or more services.
 */
export const ServiceIconShell = (props: ServiceIconShellProps) => {
  return (
    <span css={Styles.root(props)} data-testid="ServiceIconShell">
      {props.children}
    </span>
  );
};
